import React, {useCallback, useMemo} from "react";
import PropTypes from "prop-types";
import {Box, Button, Chip, Divider, Stack, Typography} from "@mui/material";
import {toast} from "react-toastify";
import {useDaapSaveSubscriptions} from "../hooks/DaapSubscription.hooks.jsx";
import SubscriptionTable from "./SubscriptionTable.jsx";

const summaryCardSx = {
    p: 2, bgcolor: "#fff", borderRadius: 1, boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
};

const emptyDraft = {
    added: new Map(),
    removed: new Map(),
};

export default function DaapAipSubPrevEdit({
                                                        selectedCompany,
                                                        selectedContactEmails,
                                                        geoDraft,
                                                        subcatDraft,
                                                        geoDraftRows,
                                                        subcatDraftRows,
                                                        deleteGeo,
                                                        deleteSubcat,
                                                        isInitialDataLoading,
                                                        resetForm,
                                                    }) {
    const [daapSaveSubscriptions, {loading: isSavingSubscriptions}] = useDaapSaveSubscriptions();

    const companyName = selectedCompany?.displayName || "N/A";
    const contactCount = selectedContactEmails.length;

    const hasChanges = geoDraft.added.size > 0 ||
        geoDraft.removed.size > 0 ||
        subcatDraft.added.size > 0 ||
        subcatDraft.removed.size > 0;

    const geoColumns = useMemo(() => [{
        field: "email", headerName: "SAVED WITH (EMAIL)", flex: 1, minWidth: 200,
    }, {
        field: "geography", headerName: "GEOGRAPHY", flex: 1, renderCell: (params) => (<Chip
            label={params.value}
            size="small"
            variant="outlined"
            color="primary"
        />),
    },], []);

    const subcategoryColumns = useMemo(() => [{
        field: "email", headerName: "SAVED WITH (EMAIL)", flex: 1, minWidth: 200,
    }, {
        field: "subcategory", headerName: "SUBCATEGORY", flex: 1, renderCell: (params) => (<Chip
            label={params.value}
            size="small"
            variant="outlined"
            color="secondary"
        />),
    },], []);

    const handleSaveSubscription = useCallback(async () => {
        try {

            const geographies = geoDraftRows
                .filter((row) => !row.isMarkedForDeletion)
                .map(({email, geography}) => ({
                    email,
                    geography,
                }));

            const removedGeographies = Array.from(geoDraft.removed.values()).map(({email, geography}) => ({
                email,
                geography,
            }));

            const subcategories = subcatDraftRows
                .filter((row) => !row.isMarkedForDeletion)
                .map(({email, subcategory}) => ({
                    email,
                    subcategory,
                }));

            const removedSubcategories = Array.from(subcatDraft.removed.values()).map(({email, subcategory}) => ({
                email,
                subcategory,
            }));

            const response = await daapSaveSubscriptions({
                variables: {
                    deletedGeographies: removedGeographies,
                    deletedSubcategories: removedSubcategories,
                    geographies,
                    subcategories,
                }
            });

            const result = response?.data?.sdnoDaapUpdateSubscription;

            if (result?.success) {
                toast.success(result.message || "Subscriptions saved successfully.");
                resetForm?.();
                return;
            }

            toast.error(
                result?.message || "Failed to save subscriptions. Please try again."
            );
        } catch {
            toast.error("Failed to save subscriptions. Please try again.");
        }
    }, [daapSaveSubscriptions, geoDraft, geoDraftRows, subcatDraft, subcatDraftRows, resetForm,]);

    return (<>
        <Box marginTop={1}>
            <Stack spacing={3}>
                <Typography variant="h6" fontWeight="bold">
                    Preview & Edit Subscriptions
                </Typography>

                <Box sx={summaryCardSx}>
                    <Stack direction="row" spacing={4} sx={{mb: 1}}>
                        <Box>
                            <Typography variant="caption" color="text.secondary" display="block">
                                COMPANY
                            </Typography>
                            <Typography variant="body2" fontWeight={500}>
                                {companyName}
                            </Typography>
                        </Box>

                        <Box>
                            <Typography variant="caption" color="text.secondary" display="block">
                                SELECTED CONTACTS
                            </Typography>
                            <Typography variant="body2" fontWeight={500}>
                                {contactCount} Contact(s)
                            </Typography>
                        </Box>
                    </Stack>

                    <Divider sx={{flexGrow: 1}}/>

                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{fontStyle: "italic"}}
                    >
                        Output is stored as two independent security datasets: (1) Email +
                        Geography, and (2) Email + Subcategory.
                    </Typography>
                </Box>

                <SubscriptionTable
                    title="Geography Mappings"
                    rows={isSavingSubscriptions || isInitialDataLoading ? [] : geoDraftRows}
                    columns={geoColumns}
                    onDeleteRow={deleteGeo}
                    loading={isSavingSubscriptions || isInitialDataLoading}
                />

                <SubscriptionTable
                    title="Subcategory Mappings"
                    rows={isSavingSubscriptions || isInitialDataLoading ? [] : subcatDraftRows}
                    columns={subcategoryColumns}
                    onDeleteRow={deleteSubcat}
                    loading={isSavingSubscriptions || isInitialDataLoading}
                />

                <Button
                    className="save-button"
                    variant="contained"
                    disabled={!hasChanges || isSavingSubscriptions}
                    onClick={handleSaveSubscription}
                >
                    {isSavingSubscriptions ? "SAVING" : "SAVE"} SUBSCRIPTIONS
                </Button>
            </Stack>
        </Box>
    </>);
}

DaapAipSubPrevEdit.propTypes = {
    selectedCompany: PropTypes.shape({
        displayName: PropTypes.string,
    }),
    selectedContactEmails: PropTypes.arrayOf(PropTypes.string),
    geoDraft: PropTypes.shape({
        added: PropTypes.instanceOf(Map),
        removed: PropTypes.instanceOf(Map),
    }),
    subcatDraft: PropTypes.shape({
        added: PropTypes.instanceOf(Map),
        removed: PropTypes.instanceOf(Map),
    }),
    geoDraftRows: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            email: PropTypes.string,
            geography: PropTypes.string,
            isMarkedForDeletion: PropTypes.bool,
            isNew: PropTypes.bool,
        })
    ),
    subcatDraftRows: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            email: PropTypes.string,
            subcategory: PropTypes.string,
            isMarkedForDeletion: PropTypes.bool,
            isNew: PropTypes.bool,
        })
    ),
    deleteGeo: PropTypes.func,
    deleteSubcat: PropTypes.func,
    isInitialDataLoading: PropTypes.bool,
    resetForm: PropTypes.func,
};

DaapAipSubPrevEdit.defaultProps = {
    selectedCompany: undefined,
    selectedContactEmails: [],
    geoDraft: emptyDraft,
    subcatDraft: emptyDraft,
    geoDraftRows: [],
    subcatDraftRows: [],
    deleteGeo: undefined,
    deleteSubcat: undefined,
    isInitialDataLoading: false,
    resetForm: undefined,
};
